import React from 'react';

import {
  ContactViolationCategory,
  WritingAssistContext,
} from './WritingAssistMoments.types';
import usePrefersReducedMotion from './usePrefersReducedMotion';
import {
  ActionButton,
  ActionRow,
  ChipList,
  MomentPanel,
  Preview,
} from './WritingAssistMoments.styles';

interface RedactionViolation {
  category: ContactViolationCategory;
}

export interface RedactionPreviewProps {
  context: WritingAssistContext;
  redactedText: string;
  violations: RedactionViolation[];
  onSaveRedacted: (text: string) => void;
  onEdit: () => void;
}

const CATEGORY_ORDER: readonly ContactViolationCategory[] = [
  'contact_phone', 'contact_email', 'contact_url', 'contact_social',
];

const CHIP_ITEM: Record<ContactViolationCategory, string> = {
  contact_phone: 'phone number',
  contact_email: 'email address',
  contact_url: 'link',
  contact_social: 'social handle',
};

const LIST_ITEM: Record<ContactViolationCategory, string> = {
  contact_phone: 'phone numbers',
  contact_email: 'email addresses',
  contact_url: 'links',
  contact_social: 'social handles',
};

const SURFACE: Record<WritingAssistContext, string> = {
  aboutMe: 'bios',
  baseCoverLetter: 'cover letters',
  applicationCoverLetter: 'cover letters',
  jobDescription: 'job postings',
};

function joinedList(items: string[]): string {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function explanation(context: WritingAssistContext, categories: ContactViolationCategory[]): string {
  const surface = SURFACE[context];
  if (categories.length > 1) {
    return `Contact details (${joinedList(categories.map(category => LIST_ITEM[category]))}) can't go in ${surface} — connecting through TempStars keeps bookings protected. Here's how it reads without them:`;
  }
  switch (categories[0]) {
    case 'contact_phone':
      return `Phone numbers can't go in ${surface} — connecting through TempStars is how bookings stay protected for everyone. Here's how it reads without it:`;
    case 'contact_email':
      return `Email addresses can't go in ${surface} — connecting through TempStars keeps bookings protected for everyone. Here's how it reads without it:`;
    case 'contact_url':
      return `Links can't go in ${surface} — they route people off-platform, which is how bookings lose their protection. Here's how it reads without it:`;
    case 'contact_social':
      return `Social handles can't go in ${surface} — same reason as contact info. Here's how it reads without it:`;
    default:
      return '';
  }
}

const RedactionPreview: React.FC<RedactionPreviewProps> = ({
  context,
  redactedText,
  violations,
  onSaveRedacted,
  onEdit,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const categories = CATEGORY_ORDER.filter(category => (
    violations.some(violation => violation.category === category)
  ));
  if (!categories.length || typeof redactedText !== 'string') return null;

  return (
    <MomentPanel style={{ transition: reducedMotion ? 'none' : 'opacity 160ms ease' }}>
      <h2>Almost there — one thing can't come along</h2>
      <p>{explanation(context, categories)}</p>
      <ChipList>
        {categories.map(category => <li key={category}>☒ {CHIP_ITEM[category]} removed</li>)}
      </ChipList>
      <Preview>{redactedText}</Preview>
      <ActionRow>
        <ActionButton type="button" onClick={() => onSaveRedacted(redactedText)}>
          Save this version
        </ActionButton>
        <ActionButton type="button" $secondary onClick={onEdit}>
          Edit my text
        </ActionButton>
      </ActionRow>
    </MomentPanel>
  );
};

export default RedactionPreview;
